import {IAccountState} from "../types";
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {adminRequest, getReturnedObject} from "../../api/adminApi";

const initialState: IAccountState = {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    avatar: '',
    roles: [],
    permissions: [],
    token: '',
    loading: false,
    error: null,
    isAuthenticated: false,
    loginCount: 0,
    lastLoginAt: null,
    otpEmail: null,
    otpExpiresAt: null,
    requiresTwoFactor: false
};

export const requestAdminLogin = createAsyncThunk("requestAdminLogin", async (data: { email: string, password: string }) => {
    return adminRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify(data)
    });
});

export const verifyAdminOtp = createAsyncThunk("verifyAdminOtp", async (data: { email: string, code: string }) => {
    return adminRequest("/auth/verify-otp", {
        method: "POST",
        body: JSON.stringify(data)
    });
});

export const getToken = requestAdminLogin;

const getAuthValue = (source: any, camelKey: string, pascalKey: string) => {
    return source?.[camelKey] ?? source?.[pascalKey];
};

const getPermissions = (source: any) => {
    const permissions = getAuthValue(source, "permissions", "Permissions");
    if (Array.isArray(permissions)) {
        return permissions;
    }

    const claims = getAuthValue(source, "associatedClaims", "AssociatedClaims");
    if (!claims || typeof claims !== "object") {
        return [];
    }

    return Object.entries(claims)
        .filter(([key]) => key === "admin_permission" || key.endsWith("/admin_permission"))
        .map(([, value]) => String(value));
};

const accountSlice = createSlice({
    name: 'account',
    initialState,
    reducers: {
        logout: (state) => {
            state.id = '';
            state.firstName = '';
            state.lastName = '';
            state.email = '';
            state.avatar = '';
            state.roles = [];
            state.permissions = [];
            state.token = '';
            state.isAuthenticated = false;
            state.error = null;
            state.loading = false;
            state.otpEmail = null;
            state.otpExpiresAt = null;
            state.requiresTwoFactor = false;
        },
        resetLoginChallenge: (state) => {
            state.otpEmail = null;
            state.otpExpiresAt = null;
            state.requiresTwoFactor = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(requestAdminLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(requestAdminLogin.fulfilled, (state, action) => {
                const payload: any = action.payload;
                if (!payload) return;

                if (payload.isSuccessfull === false || payload.isSuccessful === false) {
                    state.loading = false;
                    state.error = payload.message;
                    return;
                }

                const challenge = getReturnedObject(payload);
                state.loading = false;
                state.error = null;

                const token = getAuthValue(challenge, "token", "Token");
                const isOtpBypass = Boolean(getAuthValue(challenge, "isOtpBypass", "IsOtpBypass"));

                if (token || isOtpBypass) {
                    const roles = getAuthValue(challenge, "roles", "Roles") || [];
                    const permissions = getPermissions(challenge);

                    state.id = getAuthValue(challenge, "id", "Id");
                    state.firstName = getAuthValue(challenge, "firstName", "FirstName");
                    state.lastName = getAuthValue(challenge, "lastName", "LastName");
                    state.email = getAuthValue(challenge, "email", "Email");
                    state.avatar = getAuthValue(challenge, "avatar", "Avatar");
                    state.roles = roles;
                    state.permissions = permissions;
                    state.token = token;
                    state.isAuthenticated = Boolean(token);
                    state.loginCount = (state.loginCount || 0) + 1;
                    state.lastLoginAt = new Date().toISOString();
                    state.otpEmail = null;
                    state.otpExpiresAt = null;
                    state.requiresTwoFactor = false;
                    return;
                }

                state.otpEmail = challenge?.email || null;
                state.otpExpiresAt = challenge?.expiresAt || null;
                state.requiresTwoFactor = Boolean(challenge?.requiresTwoFactor ?? true);
            })
            .addCase(requestAdminLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'An error occurred';
            })
            .addCase(verifyAdminOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(verifyAdminOtp.fulfilled, (state, action) => {
                const admin = getReturnedObject(action.payload);
                const roles = getAuthValue(admin, "roles", "Roles") || [];
                const permissions = getPermissions(admin);
                const token = getAuthValue(admin, "token", "Token");

                state.loading = false;
                state.error = null;
                state.id = getAuthValue(admin, "id", "Id");
                state.firstName = getAuthValue(admin, "firstName", "FirstName");
                state.lastName = getAuthValue(admin, "lastName", "LastName");
                state.email = getAuthValue(admin, "email", "Email");
                state.avatar = getAuthValue(admin, "avatar", "Avatar");
                state.roles = roles;
                state.permissions = permissions;
                state.token = token;
                state.isAuthenticated = Boolean(token);
                state.loginCount = (state.loginCount || 0) + 1;
                state.lastLoginAt = new Date().toISOString();
                state.otpEmail = null;
                state.otpExpiresAt = null;
                state.requiresTwoFactor = false;
            })
            .addCase(verifyAdminOtp.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'An error occurred';
            });
    }
});

export const {logout, resetLoginChallenge} = accountSlice.actions;

export default accountSlice.reducer;
